/**
 * CropCycleHistory.tsx — Historial de ciclos del lote (D17), solo lectura: todas las zonas
 * o, si el lote no tiene zonas, el lote mismo. Cerrar un ciclo se hace en la tabla de
 * Asignación, nunca aquí.
 */
import {
  makeStyles, tokens, Text, Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useT } from '../../i18n';
import { useIntlFormat } from '../../hooks/useIntlFormat';
import { fetchLoteCropCycles, type CropCycle } from '../../api/crop.api';
import { resolveEntityName } from '../../utils/entity-names';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, marginTop: tokens.spacingVerticalL },
});

const NO_NAMES: ReadonlyMap<string, string> = new Map();

export function sortCyclesForHistory(cycles: CropCycle[]): CropCycle[] {
  return [...cycles].sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''));
}

export function CropCycleHistory({ lote, modules, crops }: {
  lote: { loteId: string; name: string };
  modules: { moduleId: string; name?: string; moduleCode: string }[];
  crops: { cropId: string; name: string }[];
}) {
  const t = useT();
  const s = useStyles();
  const { fmtDateOnly } = useIntlFormat();
  // Misma queryKey que CropAssignmentPanel: no genera un request extra.
  const { data: cycles = [] } = useQuery({ queryKey: ['crop-cycles', lote.loteId], queryFn: () => fetchLoteCropCycles(lote.loteId) });
  const zoneName = (c: CropCycle) => {
    if (c.targetType === 'LOTE') return lote.name;
    const m = modules.find(x => x.moduleId === c.targetId);
    return m ? (m.name || m.moduleCode) : resolveEntityName(c.targetId, NO_NAMES);
  };
  const cropName = (id: string) => crops.find(c => c.cropId === id)?.name ?? resolveEntityName(id, NO_NAMES);
  const rows = sortCyclesForHistory(cycles);

  return (
    <div className={s.root}>
      <Text weight="semibold">{t.crop.historyTitle}</Text>
      {rows.length === 0 ? (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>{t.crop.historyEmpty}</Text>
      ) : (
        <Table size="small" aria-label={t.crop.historyTitle}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t.crop.cicloLabel}</TableHeaderCell>
              <TableHeaderCell>{t.crop.historyZone}</TableHeaderCell>
              <TableHeaderCell>{t.crop.assignCropLabel}</TableHeaderCell>
              <TableHeaderCell>{t.crop.historyStatus}</TableHeaderCell>
              <TableHeaderCell>{t.crop.assignDateLabel}</TableHeaderCell>
              <TableHeaderCell>{t.crop.historyClosedAt}</TableHeaderCell>
              <TableHeaderCell>{t.crop.historyClosedBy}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(c => (
              <TableRow key={`${c.targetId}:${c.ciclo}`}>
                <TableCell>{c.ciclo}</TableCell>
                <TableCell>{zoneName(c)}</TableCell>
                <TableCell>{cropName(c.cropId)}</TableCell>
                <TableCell>{c.status === 'OPEN' ? t.crop.cycleOpen : t.crop.cycleClosed}</TableCell>
                <TableCell>{c.plantingDate ?? '—'}</TableCell>
                <TableCell>{c.closedAt ? fmtDateOnly(c.closedAt) : '—'}</TableCell>
                <TableCell>{c.closedBy ? resolveEntityName(c.closedBy, NO_NAMES, c.closedByName) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
